import { c as _c } from "react/compiler-runtime";
import { AnalyticsCategoryNavigation } from "@components/analytics/in-house/analytics-category-navigation";
import { AnalyticsChart } from "@components/analytics/in-house/analytics-chart";
import { AnalyticsProjectsBreakdown } from "@components/analytics/in-house/analytics-projects-breakdown";
import { AnalyticsSearchFilter } from "@components/analytics/in-house/analytics-search-filter";
import { DEFAULT_METRIC_KEY } from "@components/analytics/in-house/constants/analytics-constants";
import { Stack } from "@mui-wrapper";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { AnalyticsEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { formatRangeLengthInDays } from "@components/date-range-picker/date-range-picker-utils";
import { ErrorPageBase } from "@pages/error-page-base";
import NotFoundIcon from "@assets/illustrations/page-not-found_566px.svg";
import { SphereSupportLink } from "@components/common/sphere-support-link";
import { ContactSupportLocations } from "@custom-types/types";
import { useAppSelector } from "@store/store-helper";
import { inHouseAnalyticsDashboardFetchingStatusSelector } from "@store/analytics/analytics-selector";

// Range of 30 days is the default for the analytics page, so we use it to initialize the selected range.
const RANGE_DAYS_30 = 30;

/** Gap between sections in the in-house analytics layout (MUI spacing units) */
const IN_HOUSE_ANALYTICS_SECTION_GAP = 2;

/**
 * Composes the in-house analytics page sections and owns shared range and metric state.
 * The selected range scopes the data; the selected metric drives the KPI bar and chart.
 */
export function InHouseAnalyticsPage() {
  const $ = _c(19);
  const {
    trackEvent
  } = useTrackEvent();
  const fetchingStatus = useAppSelector(inHouseAnalyticsDashboardFetchingStatusSelector);
  const [selectedMetricKey, setSelectedMetricKey] = useState(DEFAULT_METRIC_KEY);
  const [selectedRange, setSelectedRange] = useState(_temp);
  let t0;
  if ($[0] !== trackEvent) {
    t0 = range => {
      setSelectedRange(range);
      const timeFrameLength = formatRangeLengthInDays(range);
      if (!timeFrameLength) {
        return;
      }
      trackEvent({
        name: AnalyticsEvents.changeDateRange,
        props: {
          timeFrameLength
        }
      });
    };
    $[0] = trackEvent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleSelectedRangeChange = t0;
  if (fetchingStatus === "rejected") {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        mt: 1.5
      };
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Stack sx={t1}><ErrorPageBase title="Failed to load the analytics page" iconImg={NotFoundIcon}>Please reload the page and try again. Contact support for assistance.{" "}<SphereSupportLink location={ContactSupportLocations.inHouseAnalytics} /></ErrorPageBase></Stack>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: "100%",
      gap: IN_HOUSE_ANALYTICS_SECTION_GAP,
      py: IN_HOUSE_ANALYTICS_SECTION_GAP
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gap: IN_HOUSE_ANALYTICS_SECTION_GAP,
      flexWrap: "wrap"
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== handleSelectedRangeChange || $[7] !== selectedRange) {
    t3 = <Stack direction="row" sx={t2}><AnalyticsSearchFilter value={selectedRange} onChange={handleSelectedRangeChange} /></Stack>;
    $[6] = handleSelectedRangeChange;
    $[7] = selectedRange;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  let t5;
  let t6;
  if ($[9] !== selectedMetricKey || $[10] !== selectedRange) {
    t4 = <AnalyticsCategoryNavigation selectedRange={selectedRange} selectedMetricKey={selectedMetricKey} onSelectMetric={setSelectedMetricKey} />;
    t5 = <AnalyticsChart selectedRange={selectedRange} metricKey={selectedMetricKey} />;
    t6 = <AnalyticsProjectsBreakdown selectedRange={selectedRange} selectedMetricKey={selectedMetricKey} />;
    $[9] = selectedMetricKey;
    $[10] = selectedRange;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = <Stack sx={t1}>{t3}{t4}{t5}{t6}</Stack>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function _temp() {
  const today = DateTime.now();
  return [today.minus({
    days: RANGE_DAYS_30 - 1
  }).startOf("day").toJSDate(), today.endOf("day").toJSDate()];
}